if (!customElements.get('glim-fit-match')) {
  class GlimFitMatch extends HTMLElement {
    connectedCallback() {
      if (this.initialized || !this.querySelector('[data-glim-source]')) return;
      this.initialized = true;

      try {
        this.source = JSON.parse(this.querySelector('[data-glim-source]').textContent);
        this.candidates = JSON.parse(this.querySelector('[data-glim-candidates]').textContent);
      } catch (error) {
        console.warn('Glim option finder could not read its product data.', error);
        return;
      }

      this.overlay = this.querySelector('.glim-fit-match__overlay');
      this.dialog = this.querySelector('.glim-fit-match__dialog');
      this.launch = this.querySelector('.glim-fit-match__launch');
      this.closeButton = this.querySelector('.glim-fit-match__close');
      this.choices = this.querySelector('.glim-fit-match__choices');
      this.results = this.querySelector('.glim-fit-match__results');
      this.status = this.querySelector('.glim-fit-match__status');
      this.selected = { size: '', color: '' };
      this.optionIndexes = {
        size: this.numberOrNull(this.dataset.sizeIndex),
        color: this.numberOrNull(this.dataset.colorIndex)
      };
      this.unavailableVariants = (this.source.variants || []).filter((variant) => !variant.available);

      this.renderChoices();
      this.launch.addEventListener('click', () => this.open());
      this.closeButton.addEventListener('click', () => this.close());
      this.overlay.addEventListener('click', (event) => {
        if (event.target === this.overlay) this.close();
      });
      this.addEventListener('keydown', (event) => this.handleKeydown(event));
    }

    numberOrNull(value) {
      return value === '' || value == null ? null : Number(value);
    }

    valuesFor(type) {
      const index = this.optionIndexes[type];
      if (index === null) return [];
      return [...new Set(this.unavailableVariants.map((variant) => variant.options[index]).filter(Boolean))];
    }

    renderChoices() {
      this.choices.innerHTML = '';
      ['size', 'color'].forEach((type) => {
        const values = this.valuesFor(type);
        if (!values.length) return;

        const group = document.createElement('div');
        group.className = 'glim-fit-match__choice-group';
        const title = document.createElement('span');
        title.className = 'glim-fit-match__choice-title';
        title.textContent = `Unavailable ${type}`;
        const row = document.createElement('div');
        row.className = 'glim-fit-match__choice-row';

        values.forEach((value) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'glim-fit-match__chip';
          button.dataset.type = type;
          button.dataset.value = value;
          button.textContent = value;
          button.setAttribute('aria-pressed', 'false');
          button.addEventListener('click', () => this.selectChoice(type, value));
          row.appendChild(button);
        });

        group.append(title, row);
        this.choices.appendChild(group);
      });
    }

    selectChoice(type, value) {
      this.selected[type] = this.selected[type] === value ? '' : value;
      this.updateChoiceState();
      this.renderResults();
    }

    updateChoiceState() {
      this.querySelectorAll('.glim-fit-match__chip').forEach((button) => {
        const { type, value } = button.dataset;
        const selected = this.selected[type] === value;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', String(selected));

        const otherType = type === 'size' ? 'color' : 'size';
        const otherValue = this.selected[otherType];
        const typeIndex = this.optionIndexes[type];
        const otherIndex = this.optionIndexes[otherType];
        const validCombination = !otherValue || this.unavailableVariants.some((variant) => (
          variant.options[typeIndex] === value && variant.options[otherIndex] === otherValue
        ));
        button.disabled = !validCombination;
      });
    }

    aliases(type) {
      const raw = type === 'size' ? this.dataset.sizeNames : this.dataset.colorNames;
      return (raw || '').toLowerCase().split(',').map((name) => name.trim()).filter(Boolean);
    }

    candidateOptionIndex(candidate, type) {
      const aliases = this.aliases(type);
      return (candidate.options || []).findIndex((option) => {
        const name = typeof option === 'string' ? option : option?.name;
        return aliases.includes(String(name || '').toLowerCase().trim());
      });
    }

    findMatchingVariant(candidate) {
      const sizeIndex = this.candidateOptionIndex(candidate, 'size');
      const colorIndex = this.candidateOptionIndex(candidate, 'color');

      return (candidate.variants || []).find((variant) => {
        if (!variant.available) return false;
        if (this.selected.size && (sizeIndex < 0 || variant.options[sizeIndex] !== this.selected.size)) return false;
        if (this.selected.color && (colorIndex < 0 || variant.options[colorIndex] !== this.selected.color)) return false;
        return true;
      });
    }

    renderResults() {
      this.results.innerHTML = '';
      if (!this.selected.size && !this.selected.color) {
        this.status.textContent = 'Choose an unavailable option to begin.';
        return;
      }

      const matches = this.candidates
        .filter((candidate) => String(candidate.id) !== String(this.source.id))
        .map((candidate) => ({ candidate, variant: this.findMatchingVariant(candidate) }))
        .filter((item) => item.variant)
        .slice(0, Number(this.dataset.resultLimit || 6));

      const requested = [this.selected.size, this.selected.color].filter(Boolean).join(' / ');
      this.status.textContent = matches.length
        ? `${matches.length} available ${matches.length === 1 ? 'match' : 'matches'} for ${requested}`
        : `No exact matches found for ${requested}`;

      if (!matches.length) {
        const empty = document.createElement('div');
        empty.className = 'glim-fit-match__empty';
        empty.innerHTML = '<strong>No exact Glim match—yet.</strong><p>Try another option or check back after the next collection drop.</p>';
        this.results.appendChild(empty);
        return;
      }

      matches.forEach(({ candidate, variant }) => this.results.appendChild(this.createCard(candidate, variant, requested)));
    }

    createCard(product, variant, requested) {
      const card = document.createElement('a');
      const root = window.Shopify?.routes?.root || '/';
      card.className = 'glim-fit-match__card';
      card.href = `${root}products/${product.handle}?variant=${variant.id}`;

      const imageWrap = document.createElement('div');
      imageWrap.className = 'glim-fit-match__card-image';
      const imageData = variant.featured_image || product.featured_image || product.images?.[0];
      const imageUrl = typeof imageData === 'string' ? imageData : imageData?.src;
      if (imageUrl) {
        const image = document.createElement('img');
        image.src = this.resizeImage(imageUrl, 640);
        image.alt = imageData?.alt || product.title || '';
        image.loading = 'lazy';
        image.width = 640;
        image.height = 800;
        imageWrap.appendChild(image);
      }
      const badge = document.createElement('span');
      badge.className = 'glim-fit-match__badge';
      badge.textContent = 'Exact option available';
      imageWrap.appendChild(badge);

      const vendor = document.createElement('span');
      vendor.className = 'glim-fit-match__vendor';
      vendor.textContent = product.vendor || '';
      vendor.hidden = this.dataset.showVendor !== 'true';
      const title = document.createElement('span');
      title.className = 'glim-fit-match__card-title';
      title.textContent = product.title;
      const meta = document.createElement('span');
      meta.className = 'glim-fit-match__card-meta';
      const price = document.createElement('span');
      price.textContent = this.formatMoney(variant.price);
      const option = document.createElement('span');
      option.className = 'glim-fit-match__card-option';
      option.textContent = requested;
      meta.append(price, option);
      card.append(imageWrap, vendor, title, meta);
      return card;
    }

    resizeImage(url, width) {
      try {
        const parsed = new URL(url, window.location.origin);
        parsed.searchParams.set('width', width);
        return parsed.toString();
      } catch (error) {
        return url;
      }
    }

    formatMoney(cents) {
      if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents, this.dataset.moneyFormat);
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: this.dataset.currency || 'USD'
      }).format(Number(cents) / 100);
    }

    open() {
      this.lastFocus = document.activeElement;
      this.overlay.hidden = false;
      this.overlay.classList.add('is-open');
      document.body.classList.add('glim-match-open');
      this.dialog.focus();
    }

    close() {
      this.overlay.classList.remove('is-open');
      this.overlay.hidden = true;
      document.body.classList.remove('glim-match-open');
      this.lastFocus?.focus();
    }

    handleKeydown(event) {
      if (this.overlay.hidden) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [...this.dialog.querySelectorAll('button:not([disabled]), a[href]')];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  customElements.define('glim-fit-match', GlimFitMatch);
}
